"use client";

import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useBibStrings } from "@/components/citation-tool/tool-strings";
import { StyleCombobox } from "@/components/style-combobox";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getFeaturedStyles } from "@/data/citation-styles";
import { trackBibliographyRestyled } from "@/lib/analytics";
import {
  applyRestyle,
  getStyleBreakdown,
  type SavedCitation,
} from "@/lib/bibliography";
import { t } from "@/lib/i18n";
import { restyleCitations } from "@/lib/restyle";

/** Sentinel for splitting a translated sentence around its action word. */
const ACTION_SLOT = "\u0000";

interface RestyleBibliographyProps {
  citations: SavedCitation[];
  /** Display name for a style slug, so the label matches the rest of the dialog. */
  getStyleName: (slug: string) => string;
  /** Called after a successful conversion so the dialog can reload from storage. */
  onRestyled: () => void;
  /** Bibliography-level CSL output language used for the reformatted list. */
  preferredLocale: string;
}

/**
 * "Reformat everything in one style" for a saved bibliography.
 *
 * Saved citations keep their parsed entry, so the whole list can be re-rendered in
 * any of the 10,834 styles without re-fetching a source. Two problems this solves:
 * switching styles mid-project used to mean deleting and re-citing every reference,
 * and a bibliography built across several sittings could silently be half APA and
 * half MLA with nothing in the UI pointing at it.
 */
export function RestyleBibliography({
  citations,
  getStyleName,
  onRestyled,
  preferredLocale,
}: RestyleBibliographyProps) {
  const breakdown = getStyleBreakdown(citations);
  const bib = useBibStrings();
  const [target, setTarget] = useState<string>(breakdown.dominant ?? "apa");
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const running = progress !== null;

  async function handleRestyle() {
    if (citations.length === 0) {
      return;
    }
    setProgress({ done: 0, total: citations.length });
    try {
      const formatted = await restyleCitations(citations, target, {
        lang: preferredLocale,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      const { updated, failed } = applyRestyle(
        target,
        formatted,
        preferredLocale
      );
      onRestyled();
      setOpen(false);
      trackBibliographyRestyled(target, updated);

      if (updated === 0) {
        toast.error(
          `Could not reformat in ${getStyleName(target)}. Your bibliography is unchanged.`
        );
      } else if (failed > 0) {
        toast.warning(
          `Reformatted ${updated} of ${updated + failed} in ${getStyleName(target)}. ${failed} could not be converted and kept their original formatting.`
        );
      } else {
        toast.success(
          `Reformatted ${updated} citation${updated === 1 ? "" : "s"} in ${getStyleName(target)}.`
        );
      }
    } catch {
      toast.error(bib.reformatFailed);
    } finally {
      setProgress(null);
    }
  }

  if (citations.length === 0) {
    return null;
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          title={bib.reformatTitle}
          variant={breakdown.distinct > 1 ? "default" : "outline"}
        >
          <RefreshCw className="size-4" />
          <span className="sr-only sm:not-sr-only">{bib.reformat}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3 p-3">
        <div>
          <p className="font-medium text-foreground text-sm">
            {bib.reformatHeading}
          </p>
          <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
            {t(bib.reformatExplain, { count: citations.length })}
          </p>
        </div>

        <StyleCombobox
          featuredStyles={getFeaturedStyles()}
          onChange={setTarget}
          placeholder={bib.reformatChoose}
          value={target}
        />

        <Button
          className="w-full"
          disabled={running}
          onClick={handleRestyle}
          size="sm"
        >
          {running ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Reformatting {progress.done}/{progress.total}…
            </>
          ) : (
            `Reformat in ${getStyleName(target)}`
          )}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Full-width banner warning that the saved citations are not all in one style.
 *
 * Rendered separately from the Reformat button because it belongs above the toolbar,
 * not inside its flex row — nesting it there squeezed it into a narrow column and
 * pushed the export controls off the edge of the dialog.
 */
export function MixedStyleNotice({
  citations,
  getStyleName,
}: {
  citations: SavedCitation[];
  getStyleName: (slug: string) => string;
}) {
  // Hooks first: the early return below is conditional, and React requires every hook
  // to run on every render.
  const bib = useBibStrings();
  // The action word stays bold, so the sentence is split around its placeholder rather
  // than interpolated — otherwise the emphasis would have to live inside the translated
  // string, where every translator would have to reproduce markup.
  const [actionBefore, actionAfter] = t(bib.mixedStylesAction, {
    action: ACTION_SLOT,
  }).split(ACTION_SLOT);

  const breakdown = getStyleBreakdown(citations);
  if (breakdown.distinct < 2) {
    return null;
  }
  const summary = Object.entries(breakdown.counts)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([slug, count]) => `${count} ${getStyleName(slug)}`)
    .join(", ");

  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
      <AlertTriangle
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-500"
      />
      <p className="text-foreground leading-relaxed">
        {t(bib.mixedStyles, {
          count: breakdown.distinct,
          styles: summary,
        })}{" "}
        {actionBefore}
        <span className="font-medium">{bib.reformat}</span>
        {actionAfter}
      </p>
    </div>
  );
}
